/* eslint-disable */
import { Suspense, useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Stack from '@mui/material/Stack';
import Container from '@mui/material/Container';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';
import { useSettingsContext } from 'src/components/settings';
import { MANAGEMENT_ROLES } from 'src/utils/user-types';
import { useAuthContext } from 'src/auth/hooks';
import { getStorage } from 'src/hooks/use-local-storage';
import { useListCorporations } from 'src/api/corporations';
import { LoadingScreen } from 'src/components/loading-screen';
import Iconify from 'src/components/iconify';

import LogisticsSelectCorporation from './logistics-select-corporation';
import LogisticsFleetBanner from './logistics-fleet-banner';

// ----------------------------------------------------------------------

// Roles allowed to see the combined vehicles + geofences map.
const COMBINED_MAP_ROLES = MANAGEMENT_ROLES;

const TABS = [
  { value: 'overview',  label: 'Visão Geral',    path: paths.dashboard.logistics.root,      icon: 'solar:widget-bold-duotone' },
  { value: 'map',       label: 'Mapa da Frota',  path: paths.dashboard.logistics.map,       icon: 'solar:map-point-bold-duotone' },
  // Tab oculta por enquanto — acesso só direto pela URL (/logistics/combined-map)
  // para evitar confusão. Descomentar para reativar na navegação.
  // { value: 'combined',  label: 'Veículos + Cercas', path: paths.dashboard.logistics.combinedMap, icon: 'solar:map-bold-duotone', roles: COMBINED_MAP_ROLES },
  { value: 'kanban',    label: 'Kanban',         path: paths.dashboard.logistics.kanban,    icon: 'solar:clipboard-list-bold-duotone' },
  { value: 'locations', label: 'Localidades',    path: paths.dashboard.logistics.locations, icon: 'solar:streets-map-point-bold-duotone' },
  { value: 'trips',     label: 'Viagens',        path: paths.dashboard.logistics.trips,     icon: 'solar:routing-2-bold-duotone' },
  { value: 'route-analysis', label: 'Análise de Rotas', path: paths.dashboard.logistics.routeAnalysis, icon: 'solar:map-arrow-square-bold-duotone' },
  // Gestão de frota — restrita a papéis de gestão (MANAGEMENT_ROLES).
  { value: 'drivers',   label: 'Motoristas',     path: paths.dashboard.logistics.drivers,   icon: 'solar:user-id-bold-duotone', roles: MANAGEMENT_ROLES },
  { value: 'vehicles',  label: 'Veículos',       path: paths.dashboard.logistics.vehicles,  icon: 'solar:bus-bold-duotone', roles: MANAGEMENT_ROLES },
];

function resolveCurrent(pathname) {
  if (pathname.includes('/logistics/combined-map')) return 'combined';
  if (pathname.includes('/logistics/map')) return 'map';
  if (pathname.includes('/logistics/kanban')) return 'kanban';
  if (pathname.includes('/logistics/locations')) return 'locations';
  if (pathname.includes('/logistics/route-analysis')) return 'route-analysis';
  if (pathname.includes('/logistics/trips')) return 'trips';
  if (pathname.includes('/logistics/drivers')) return 'drivers';
  if (pathname.includes('/logistics/vehicles')) return 'vehicles';
  return 'overview';
}

const getDefaultCorporationId = (corporations) => {
  if (!corporations?.length) return null;
  if (corporations.length === 1) return corporations[0].id;
  const storedId = getStorage('main_corporation_id');
  if (!storedId) return null;
  return corporations.find((c) => c.id === storedId)?.id ?? null;
};

export default function LogisticsTabsLayout() {
  const settings = useSettingsContext();
  const { user } = useAuthContext();
  const { pathname } = useLocation();
  const current = resolveCurrent(pathname);

  // Corporation selection lives here (above the submenu) so it stays put when
  // switching tabs and isn't reset by the child pages' polling queries.
  const { corporations, corporationsLoading } = useListCorporations();
  const [selectedCorporationId, setSelectedCorporationId] = useState(null);

  useEffect(() => {
    if (selectedCorporationId != null) return;
    if (!corporations?.length) return;
    setSelectedCorporationId(getDefaultCorporationId(corporations));
  }, [corporations, selectedCorporationId]);

  const visibleTabs = TABS.filter((tab) => !tab.roles || tab.roles.includes(user?.role));

  // Pages reachable only by direct URL (no tab) -> no tab selected (avoids MUI warning).
  const tabValue = visibleTabs.some((tab) => tab.value === current) ? current : false;

  return (
    <>
      <Container maxWidth={settings.themeStretch ? false : 'xl'}>
        <Stack spacing={2} sx={{ mb: { xs: 2, md: 1 } }}>
          <LogisticsFleetBanner />

          {corporations?.length > 1 && (
            <LogisticsSelectCorporation
              corporations={corporations}
              selectedCorporationId={selectedCorporationId}
              setSelectedCorporationId={setSelectedCorporationId}
            />
          )}

          <Tabs value={tabValue}>
            {visibleTabs.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                label={tab.label}
                component={RouterLink}
                href={tab.path}
                icon={<Iconify icon={tab.icon} width={20} />}
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Stack>
      </Container>

      {/* Own Suspense boundary: switching tabs only reloads the content below
          the submenu, keeping the corporation select + tabs mounted. */}
      <Suspense fallback={<LoadingScreen />}>
        <Outlet
          context={{
            corporations,
            corporationsLoading,
            selectedCorporationId,
            setSelectedCorporationId,
          }}
        />
      </Suspense>
    </>
  );
}
